/**
 * Calculo de creditos de SMS. Espelho de backend/src/utils/smsSegmentos.js.
 *
 * A doc da Comtele cita 160 caracteres e 1 credito extra a cada 153 — isso vale so
 * para GSM-7. Qualquer caractere fora do alfabeto GSM 03.38 derruba a mensagem
 * inteira para UCS-2, onde o limite cai para 70 (67 em mensagem concatenada).
 *
 * Isso importa muito aqui: quase todo texto de campanha em portugues tem acento.
 * "e" com crase e "e" com agudo estao no GSM-7, mas "a"/"o" com til e "a", "i",
 * "o", "u" com agudo e "c" cedilha minusculo NAO. Ou seja: uma mensagem de 150
 * caracteres custa 1 credito sem acento e 3 com acento. Mostrar so 160/153
 * subestimaria o custo de praticamente toda campanha.
 */

const GSM7_BASICO =
    '@£$¥èéùìòÇ\nØø\rÅå' +
    'Δ_ΦΓΛΩΠΨΣΘΞÆæßÉ' +
    ' !"#¤%&\'()*+,-./0123456789:;<=>?' +
    '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§' +
    '¿abcdefghijklmnopqrstuvwxyzäöñüà';

// Tabela de extensao: ocupam 2 posicoes cada.
const GSM7_EXTENDIDO = '^{}\\[~]|€';

const SET_BASICO = new Set(GSM7_BASICO.split(''));
const SET_EXTENDIDO = new Set(GSM7_EXTENDIDO.split(''));

const RE_DIACRITICOS = new RegExp('[\\u0300-\\u036f]', 'g');
const RE_NAO_ASCII = new RegExp('[^\\u0000-\\u007f]', 'g');

/** Caracteres do texto que forcam a mensagem para UCS-2. */
export const caracteresForaGsm7 = (texto) => {
    const fora = new Set();
    for (const ch of String(texto || '')) {
        if (!SET_BASICO.has(ch) && !SET_EXTENDIDO.has(ch)) fora.add(ch);
    }
    return Array.from(fora);
};

export const ehGsm7 = (texto) => caracteresForaGsm7(texto).length === 0;

export const calcularSegmentosSms = (texto) => {
    const str = String(texto || '');
    const fora = caracteresForaGsm7(str);
    const gsm7 = fora.length === 0;

    let unidades = 0;
    if (gsm7) {
        for (const ch of str) unidades += SET_EXTENDIDO.has(ch) ? 2 : 1;
    } else {
        unidades = str.length;
    }

    const limiteUnico = gsm7 ? 160 : 70;
    const limiteConcatenado = gsm7 ? 153 : 67;

    let segmentos;
    if (unidades === 0) segmentos = 0;
    else if (unidades <= limiteUnico) segmentos = 1;
    else segmentos = Math.ceil(unidades / limiteConcatenado);

    const capacidade = segmentos > 1 ? segmentos * limiteConcatenado : limiteUnico;

    return {
        encoding: gsm7 ? 'GSM-7' : 'UCS-2',
        caracteres: Array.from(str).length,
        unidades,
        segmentos,
        limiteSegmento: segmentos > 1 ? limiteConcatenado : limiteUnico,
        restanteNoSegmento: Math.max(0, capacidade - unidades),
        caracteresForaGsm7: fora,
    };
};

/** Troca acentos por equivalentes ASCII para trazer a mensagem de volta ao GSM-7. */
export const removerAcentos = (texto) =>
    String(texto || '')
        .normalize('NFD')
        .replace(RE_DIACRITICOS, '')
        .replace(RE_NAO_ASCII, '');
